
import * as React from "react";
import { addDays, format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import { ru } from "date-fns/locale";
import { Calendar as CalendarIcon, X, Check } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

interface EnhancedDateRangePickerProps {
  className?: string;
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
}

const QUICK_PRESETS = [
  {
    label: "Сегодня",
    getValue: () => {
      const today = new Date();
      return { from: startOfDay(today), to: endOfDay(today) };
    }
  },
  {
    label: "Вчера",
    getValue: () => {
      const yesterday = subDays(new Date(), 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    }
  },
  {
    label: "Последние 3 дня",
    getValue: () => ({
      from: startOfDay(subDays(new Date(), 2)),
      to: endOfDay(new Date())
    })
  },
  {
    label: "Последние 7 дней",
    getValue: () => ({
      from: startOfDay(subDays(new Date(), 6)),
      to: endOfDay(new Date())
    })
  },
  {
    label: "Эта неделя",
    getValue: () => ({
      from: startOfDay(startOfWeek(new Date(), { weekStartsOn: 1 })),
      to: endOfDay(endOfWeek(new Date(), { weekStartsOn: 1 }))
    })
  },
  {
    label: "Этот месяц",
    getValue: () => ({
      from: startOfDay(startOfMonth(new Date())),
      to: endOfDay(endOfMonth(new Date()))
    })
  }
];

export function EnhancedDateRangePicker({
  className,
  date,
  setDate,
  placeholder = "Выберите диапазон дат",
  maxDate,
  minDate
}: EnhancedDateRangePickerProps) {
  const [tempDate, setTempDate] = React.useState<DateRange | undefined>(date);
  const [isOpen, setIsOpen] = React.useState(false);

  const formatDateRange = (dateRange: DateRange | undefined) => {
    if (!dateRange?.from) return placeholder;
    
    if (!dateRange.to || dateRange.from.getTime() === dateRange.to.getTime()) {
      return format(dateRange.from, "d MMMM yyyy", { locale: ru });
    }
    
    return `${format(dateRange.from, "d MMM", { locale: ru })} - ${format(dateRange.to, "d MMM yyyy", { locale: ru })}`;
  };

  const getDayCount = (dateRange: DateRange | undefined) => {
    if (!dateRange?.from || !dateRange?.to) return 0;
    const diffTime = Math.abs(dateRange.to.getTime() - dateRange.from.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handlePresetClick = (preset: typeof QUICK_PRESETS[0]) => {
    const newDate = preset.getValue();
    setTempDate(newDate);
  };

  const handleApply = () => {
    setDate(tempDate);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempDate(date);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempDate(undefined);
  };

  // Функция для проверки, выбран ли пресет
  const isPresetSelected = (preset: typeof QUICK_PRESETS[0]) => {
    if (!tempDate?.from || !tempDate?.to) return false;
    const presetRange = preset.getValue();
    return (
      tempDate.from.getTime() === presetRange.from.getTime() &&
      tempDate.to.getTime() === presetRange.to.getTime()
    );
  };

  React.useEffect(() => {
    if (isOpen) {
      setTempDate(date);
    }
  }, [isOpen, date]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span className="flex-1">{formatDateRange(date)}</span>
            {date && (
              <Badge variant="secondary" className="ml-2">
                {getDayCount(date) === 1 ? '1 день' : `${getDayCount(date)} дней`}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {/* Быстрые пресеты - увеличиваем ширину еще больше для полного отображения текста */}
            <div className="border-r p-3 space-y-1 w-[180px]">
              <div className="text-sm font-medium mb-2">Быстрый выбор</div>
              {QUICK_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant={isPresetSelected(preset) ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "w-full justify-start text-xs h-8 relative px-2",
                    isPresetSelected(preset) && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handlePresetClick(preset)}
                >
                  <span className="text-left leading-tight">{preset.label}</span>
                  {isPresetSelected(preset) && (
                    <Check className="h-3 w-3 ml-auto flex-shrink-0" />
                  )}
                </Button>
              ))}
            </div>
            
            {/* Календарь */}
            <div className="p-3">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={tempDate?.from || new Date()}
                selected={tempDate}
                onSelect={setTempDate}
                numberOfMonths={2}
                locale={ru}
                disabled={(date) => {
                  if (maxDate && date > maxDate) return true;
                  if (minDate && date < minDate) return true;
                  return false;
                }}
                className="pointer-events-auto"
              />
              
              {/* Показываем выбранный период */}
              {tempDate?.from && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-muted-foreground mb-2">Выбранный период:</div>
                  <div className="text-sm font-medium">
                    {formatDateRange(tempDate)}
                  </div>
                  {tempDate?.to && getDayCount(tempDate) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {getDayCount(tempDate) === 1 ? '1 день' : `${getDayCount(tempDate)} дней`}
                    </div>
                  )}
                </div>
              )}
              
              {/* Кнопки управления */}
              <div className="flex items-center justify-between pt-3 border-t mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Очистить
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    className="text-xs"
                  >
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApply}
                    className="text-xs"
                    disabled={!tempDate?.from}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Применить
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
